import { useMemo } from "react";
import { graphql, useFragment, useLazyLoadQuery } from "react-relay";
import type { FragmentRefs } from "relay-runtime";
import { scmpPlus } from "scmp-sdk";

import { data } from "shared/data";

import type { hooksInlinePlusWidgetContent$key } from "scmp-app/queries/__generated__/hooksInlinePlusWidgetContent.graphql";
import type { hooksTopicsByIdsQuery } from "scmp-app/queries/__generated__/hooksTopicsByIdsQuery.graphql";

import { InlinePlusWidgetEnabledTopics } from "./consts";
import { getTopicsByTopicId } from "./helpers";

export const useInlinePlusWidget = (reference?: hooksInlinePlusWidgetContent$key) => {
  const content = useFragment(
    graphql`
      fragment hooksInlinePlusWidgetContent on Content {
        topics {
          entityId
        }
      }
    `,
    reference ?? null,
  );

  const firstTopicEntityId = content?.topics?.[0]?.entityId ?? "";
  const topicIds = getTopicsByTopicId(firstTopicEntityId);

  const shouldShowInlinePlusWidget = InlinePlusWidgetEnabledTopics.includes(firstTopicEntityId);

  return {
    shouldShowInlinePlusWidget,
    topicIds,
  };
};

export const topicsByIdsQuery = graphql`
  query hooksTopicsByIdsQuery(
    $topicIds: [String!]!
    $applicationId: String!
    $first: Int = 10
    $scmpPlusPaywallTypeIds: [String]
  ) {
    topics(filter: { entityIds: $topicIds }, first: $first, applicationId: $applicationId) {
      edges {
        node {
          contents(
            first: 4
            filter: { paywallTypeIds: $scmpPlusPaywallTypeIds }
            orderBy: { field: PUBLISHED_DATE, direction: DESC }
          ) {
            edges {
              node {
                entityId
                publishedDate
                ...subscriberContentInlinePlusWidgetContent
              }
            }
          }
        }
      }
    }
  }
`;

export const useContentsByTopicsByIds = (topicIds: string[] = []) => {
  const result = useLazyLoadQuery<hooksTopicsByIdsQuery>(
    topicsByIdsQuery,
    {
      applicationId: data.application.scmp.entityUuid,
      first: topicIds.length ?? 3,
      scmpPlusPaywallTypeIds: [scmpPlus.article.paywallTypeId],
      topicIds,
    },
    { fetchPolicy: "store-or-network" },
  );

  const topics = useMemo(
    () => result.topics?.edges?.map(edge => edge.node) ?? [],
    [result.topics?.edges],
  );

  const contentsReference = useMemo(() => {
    if (!topics?.length) return [];

    const articleIds = new Set<string>();
    const allContents: Array<{
      readonly " $fragmentSpreads": FragmentRefs<"subscriberContentInlinePlusWidgetContent">;
      readonly entityId: string;
      readonly publishedDate: null | number | undefined;
    }> = [];

    outer: for (const topic of topics) {
      if (!topic?.contents?.edges) continue;

      for (const edge of topic.contents.edges) {
        const node = edge?.node;
        if (!node?.entityId || articleIds.has(node.entityId)) continue;

        articleIds.add(node.entityId);
        allContents.push(node);
      }
    }

    const sortedContents = allContents
      .sort((a, b) => {
        const dateA = a.publishedDate ?? 0;
        const dateB = b.publishedDate ?? 0;
        return dateB - dateA; // Sort by publishedDate descending
      })
      .slice(0, 4); // Take the latest 4 articles

    return sortedContents;
  }, [topics]);

  return { articleIds: contentsReference.map(reference => reference.entityId), contentsReference };
};
